import React, { useState, useEffect, useContext } from 'react';
import { KeycloakContext } from '../App';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../lib/format';

interface Ticket {
  id: string;
  service_ref?: string;
  subject: string;
  content: string;
  status: string;
  created_at: string;
}

interface Reply {
  id: string;
  ticket_id: string;
  content: string;
  is_admin: boolean;
  author_role: string;
  created_at: string;
}

interface Service {
  id: string;
  type: string;
  name?: string;
}

const statusBadge = (status: string) => {
  if (status === 'closed')
    return (
      <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-muted text-foreground">
        Closed
      </span>
    );
  return (
    <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-success/10 text-success">
      Open
    </span>
  );
};

export default function Support() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // New-ticket form
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [form, setForm] = useState({ serviceRef: '', subject: '', content: '' });
  const [creating, setCreating] = useState<boolean>(false);

  // Expanded ticket thread
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(new Set());
  const [replies, setReplies] = useState<{ [ticketId: string]: Reply[] }>({});
  const [replyInputs, setReplyInputs] = useState<{ [ticketId: string]: string }>({});
  const [sendingReply, setSendingReply] = useState<{ [ticketId: string]: boolean }>({});

  const headers: Record<string, string> = { Authorization: `Bearer ${keycloak?.token}` };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const ticketsRes = await fetch(`${API_URL}/support/tickets`, { headers });
      const ticketsData = await ticketsRes.json().catch(() => []);
      setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      setError(null);
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // Fetch services independently — failure is non-fatal
    try {
      const servicesRes = await fetch(`${API_URL}/services`, { headers });
      if (servicesRes.ok) {
        const servicesData = await servicesRes.json().catch(() => []);
        setServices(Array.isArray(servicesData) ? servicesData : []);
      }
    } catch (err: any) {
      console.warn('Could not load services list for ticket form:', err.message);
    }
  };

  // ---------------------------------------------------------------------------
  // Ticket creation
  // ---------------------------------------------------------------------------
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.content.trim()) {
      toast('Subject and message are required.', 'warning');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/support/tickets`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_ref: form.serviceRef || '',
          subject: form.subject,
          content: form.content,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to create ticket');
      }
      setShowCreate(false);
      setForm({ serviceRef: '', subject: '', content: '' });
      await fetchAll();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete ticket
  // ---------------------------------------------------------------------------
  const handleDelete = async (ticketId: string) => {
    const ok = await confirm({ title: 'Delete ticket', message: 'Delete this ticket?', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/support/tickets/${ticketId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to delete ticket');
      }
      setExpandedTickets((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
      await fetchAll();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Thread expand / replies
  // ---------------------------------------------------------------------------
  const toggleExpand = async (ticketId: string) => {
    setExpandedTickets((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
        if (!replies[ticketId]) loadReplies(ticketId);
      }
      return next;
    });
  };

  const loadReplies = async (ticketId: string) => {
    try {
      const res = await fetch(`${API_URL}/support/tickets/${ticketId}/replies`, { headers });
      const data = await res.json().catch(() => []);
      setReplies((prev) => ({ ...prev, [ticketId]: Array.isArray(data) ? data : [] }));
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleSendReply = async (ticket: Ticket) => {
    const content = (replyInputs[ticket.id] || '').trim();
    if (!content) return;
    setSendingReply((prev) => ({ ...prev, [ticket.id]: true }));
    try {
      const res = await fetch(`${API_URL}/support/tickets/${ticket.id}/replies`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || 'Failed to send reply');
      }
      setReplyInputs((prev) => ({ ...prev, [ticket.id]: '' }));
      await loadReplies(ticket.id);
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setSendingReply((prev) => ({ ...prev, [ticket.id]: false }));
    }
  };

  const formatDate = formatDateTime;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<ChatBubbleLeftRightIcon />}
          title="Support"
          description="Manage your support tickets. Open a new ticket or follow up on existing ones."
          actions={
            <button
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm"
              disabled
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              New Ticket
            </button>
          }
        />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ChatBubbleLeftRightIcon />}
        title="Support"
        description="Manage your support tickets. Open a new ticket or follow up on existing ones."
        actions={
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Ticket
          </button>
        }
      />

      {/* Create ticket form */}
      {showCreate && (
        <div className="bg-card shadow rounded-lg p-6 border border-border">
          <h2 className="text-lg font-medium text-foreground mb-4">Open a new ticket</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground">
                Related Service <span className="text-muted-foreground">(optional)</span>
              </label>
              <select
                value={form.serviceRef}
                onChange={(e) => setForm((p) => ({ ...p, serviceRef: e.target.value }))}
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
              >
                <option value="">None — General enquiry</option>
                {services.map((svc) => {
                  const label = svc.name ? `[${svc.type}] ${svc.name}` : svc.type;
                  return (
                    <option key={svc.id} value={label}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Subject *</label>
              <input
                type="text"
                required
                maxLength={500}
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                placeholder="Brief summary of your issue"
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Message *</label>
              <textarea
                required
                rows={5}
                value={form.content}
                onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                placeholder="Describe your issue in detail..."
                className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-sm font-medium text-foreground hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring disabled:opacity-50"
              >
                {creating ? 'Submitting...' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tickets list */}
      {tickets.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">No tickets</h3>
          <p className="mt-1 text-sm text-muted-foreground">Open a new ticket to get support.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => {
            const isExpanded = expandedTickets.has(ticket.id);
            const threadReplies = replies[ticket.id] || [];
            const isClosed = ticket.status === 'closed';

            return (
              <div key={ticket.id} className="bg-card shadow rounded-lg overflow-hidden border border-border">
                {/* Ticket row */}
                <div
                  className="flex items-center justify-between px-4 py-4 cursor-pointer hover:bg-muted/40"
                  onClick={() => toggleExpand(ticket.id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        #{ticket.id} — {ticket.subject}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatDate(ticket.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    {statusBadge(ticket.status)}
                    {!isClosed && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(ticket.id);
                        }}
                        className="text-red-500 hover:text-red-700"
                        title="Delete ticket"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                    {isExpanded ? (
                      <ChevronUpIcon className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Thread */}
                {isExpanded && (
                  <div className="border-t border-border px-4 py-4 space-y-4">
                    {threadReplies.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic">No messages yet.</p>
                    ) : (
                      threadReplies.map((reply) => (
                        <div
                          key={reply.id}
                          className={`flex ${reply.is_admin ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-lg px-4 py-3 text-sm ${
                              reply.is_admin
                                ? 'bg-muted text-foreground'
                                : 'bg-primary text-white'
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{reply.content}</p>
                            <p
                              className={`mt-1 text-xs ${
                                reply.is_admin ? 'text-muted-foreground' : 'text-white/70'
                              }`}
                            >
                              {reply.is_admin ? 'Support Team' : 'You'} · {formatDate(reply.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}

                    {/* Reply input */}
                    {!isClosed ? (
                      <div className="flex items-end gap-3 pt-2">
                        <textarea
                          rows={2}
                          value={replyInputs[ticket.id] || ''}
                          onChange={(e) =>
                            setReplyInputs((prev) => ({ ...prev, [ticket.id]: e.target.value }))
                          }
                          placeholder="Type your reply..."
                          className="flex-1 rounded-md border-border shadow-sm focus:border-primary focus-visible:ring-ring sm:text-sm resize-none"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendReply(ticket);
                            }
                          }}
                        />
                        <button
                          onClick={() => handleSendReply(ticket)}
                          disabled={sendingReply[ticket.id] || !replyInputs[ticket.id]?.trim()}
                          className="inline-flex items-center justify-center rounded-md bg-primary p-2.5 text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none"
                          title="Send reply (Enter)"
                        >
                          <PaperAirplaneIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic pt-2">This ticket is closed.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
